import React, { useState } from 'react';
import { Truck, ChevronDown, ChevronRight, Play, Pause, MapPin, Package, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function DriversList({ drivers, deliveryPoints, orders, activeView }) {
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [showingDeliveries, setShowingDeliveries] = useState(true);
  const [loadingStates, setLoadingStates] = useState({}); // Track loading per driver and action

  const toggleDriver = (driverId) => {
    setExpandedDriver(expandedDriver === driverId ? null : driverId);
  };

  const setLoading = (driverId, action, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [`${driverId}-${action}`]: isLoading
    }));
  };

  const isLoading = (driverId, action) => {
    return loadingStates[`${driverId}-${action}`] || false;
  };

  const getDriverDeliveries = (driverId) => {
    return deliveryPoints
      .filter(dp => dp.driver_id === driverId)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  };

  const getDriverOrders = (driverId) => {
    return orders
      .filter(order => order.driver_id === driverId)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      encamino: 'bg-blue-100 text-blue-800',
      entregado: 'bg-green-100 text-green-800',
      cancelado: 'bg-gray-100 text-gray-800',
      rechazado: 'bg-red-100 text-red-800',
      postergado: 'bg-orange-100 text-orange-800',
      completado: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const handleStartRoute = async (driverId) => {
    setLoading(driverId, 'start', true);
    
    try {
      const token = localStorage.getItem('token');
      const driver = drivers.find(d => d.id === driverId);
      
      if (!driver) {
        alert('Conductor no encontrado');
        return;
      }

      if (showingDeliveries) {
        // Handle delivery routes
        const driverDeliveries = getDriverDeliveries(driverId)
          .filter(dp => dp.status === 'pending' && dp.latitude && dp.longitude);

        if (driverDeliveries.length === 0) {
          alert('No hay entregas pendientes con ubicación para este conductor');
          return;
        }

        const startPoint = {
          lat: driver.latitude,
          lng: driver.longitude,
        };

        const endPoint = {
          lat: driver.home_latitude || driver.latitude,
          lng: driver.home_longitude || driver.longitude,
        };

        const waypoints = driverDeliveries.map(dp => ({
          lat: dp.latitude,
          lng: dp.longitude,
        }));

        const deliveryIds = driverDeliveries.map(dp => dp.id);

        await axios.post(
          `${API_URL}/api/create_route_deliveries`,
          {
            startPoint: `${startPoint.lat},${startPoint.lng}`,
            endPoint: `${endPoint.lat},${endPoint.lng}`,
            waypoints: waypoints.map(wp => `${wp.lat},${wp.lng}`).join('|'),
            deliveryIds: deliveryIds,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        alert('Ruta de entregas creada exitosamente');
      } else {
        // Handle pickup routes
        const driverOrders = getDriverOrders(driverId)
          .filter(order => order.status === 'pending' && order.address?.latitude && order.address?.longitude);

        if (driverOrders.length === 0) {
          alert('No hay recojos pendientes con ubicación para este conductor');
          return;
        }

        const startPoint = {
          lat: driver.latitude,
          lng: driver.longitude,
        };

        const endPoint = {
          lat: -12.00093305507352,
          lng: -77.05374889286432,
        };

        const waypoints = driverOrders.map(order => ({
          lat: order.address.latitude,
          lng: order.address.longitude,
        }));

        const orderIds = driverOrders.map(order => order.id);

        await axios.post(
          `${API_URL}/api/create_route`,
          {
            startPoint: `${startPoint.lat},${startPoint.lng}`,
            endPoint: `${endPoint.lat},${endPoint.lng}`,
            waypoints: waypoints.map(wp => `${wp.lat},${wp.lng}`).join('|'),
            orderIds: orderIds,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        alert('Ruta de recojos creada exitosamente');
      }
    } catch (error) {
      console.error('Error creating route:', error);
      alert('Error al crear la ruta');
    } finally {
      setLoading(driverId, 'start', false);
    }
  };

  const handlePauseRoute = async (driverId) => {
    setLoading(driverId, 'pause', true);
    
    try {
      const encaminoItems = showingDeliveries
        ? getDriverDeliveries(driverId).filter(dp => dp.status === 'encamino')
        : getDriverOrders(driverId).filter(order => order.status === 'encamino');

      const ids = encaminoItems.map(item => item.id);

      if (ids.length === 0) {
        alert('No hay entregas/recojos en camino para pausar');
        return;
      }

      const endpoint = showingDeliveries
        ? '/api/update_delivery_status'
        : '/api/update_order_status';

      await axios.put(`${API_URL}${endpoint}`, {
        ids: ids,
        status: 'pending'
      });

      alert('Ruta pausada exitosamente');
    } catch (error) {
      console.error('Error pausing route:', error);
      alert('Error al pausar la ruta');
    } finally {
      setLoading(driverId, 'pause', false);
    }
  };

  const handleResumeRoute = async (driverId) => {
    setLoading(driverId, 'resume', true);
    
    try {
      const pendingItems = showingDeliveries
        ? getDriverDeliveries(driverId).filter(dp => dp.status === 'pending')
        : getDriverOrders(driverId).filter(order => order.status === 'pending');

      const ids = pendingItems.map(item => item.id);

      if (ids.length === 0) {
        alert('No hay entregas/recojos pendientes para iniciar');
        return;
      }

      const endpoint = showingDeliveries
        ? '/api/update_delivery_status'
        : '/api/update_order_status';

      await axios.put(`${API_URL}${endpoint}`, {
        ids: ids,
        status: 'encamino'
      });

      alert('Ruta reanudada exitosamente');
    } catch (error) {
      console.error('Error resuming route:', error);
      alert('Error al reanudar la ruta');
    } finally {
      setLoading(driverId, 'resume', false);
    }
  };

  const truncateName = (name, maxLength = 12) => {
    if (!name) return 'Sin nombre';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-[#B1336E] px-4 py-3 border-b border-[#B1336E]/20">
        <h2 className="text-white font-bold text-sm flex items-center gap-2 font-montserrat">
          <Truck className="w-4 h-4" />
          Conductores ({drivers.length})
        </h2>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {drivers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <Truck className="w-12 h-12 text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm font-montserrat">
              No hay conductores disponibles
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {drivers.map((driver) => {
              const driverDeliveries = getDriverDeliveries(driver.id);
              const driverOrders = getDriverOrders(driver.id);
              const isExpanded = expandedDriver === driver.id;

              return (
                <div
                  key={driver.id}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Driver Header */}
                  <button
                    onClick={() => toggleDriver(driver.id)}
                    className="w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-[#B1336E]" />
                      <div className="text-left">
                        <p className="text-sm font-bold text-slate-800 font-montserrat">
                          {driver.name || `Conductor ${driver.id}`}
                        </p>
                        <p className="text-xs text-slate-500 font-montserrat">
                          Entregas: {driverDeliveries.length} | Recojos: {driverOrders.length}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-200">
                      {/* Toggle Buttons */}
                      <div className="flex gap-1 p-2 bg-slate-50">
                        <button
                          onClick={() => setShowingDeliveries(true)}
                          className={`flex-1 py-1.5 px-2 text-xs rounded transition-colors font-montserrat ${
                            showingDeliveries
                              ? 'bg-[#B1336E] text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <MapPin className="w-3 h-3 inline mr-1" />
                          Entregas
                        </button>
                        <button
                          onClick={() => setShowingDeliveries(false)}
                          className={`flex-1 py-1.5 px-2 text-xs rounded transition-colors font-montserrat ${
                            !showingDeliveries
                              ? 'bg-[#B1336E] text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Package className="w-3 h-3 inline mr-1" />
                          Recojos
                        </button>
                      </div>

                      {/* Route Controls */}
                      <div className="flex gap-1 p-2 bg-slate-50 border-t border-slate-200">
                        <button
                          onClick={() => handleStartRoute(driver.id)}
                          disabled={isLoading(driver.id, 'start')}
                          className="flex-1 py-1.5 px-2 bg-[#67A43C] hover:bg-[#67A43C]/80 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs rounded transition-colors flex items-center justify-center gap-1 font-montserrat"
                        >
                          {isLoading(driver.id, 'start') ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Play className="w-3 h-3" />
                          )}
                          {isLoading(driver.id, 'start') ? 'Creando...' : 'Crear Ruta'}
                        </button>
                        <button
                          onClick={() => handlePauseRoute(driver.id)}
                          disabled={isLoading(driver.id, 'pause')}
                          className="flex-1 py-1.5 px-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs rounded transition-colors flex items-center justify-center gap-1 font-montserrat"
                        >
                          {isLoading(driver.id, 'pause') ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Pause className="w-3 h-3" />
                          )}
                          {isLoading(driver.id, 'pause') ? 'Pausando...' : 'Pausar'}
                        </button>
                        <button
                          onClick={() => handleResumeRoute(driver.id)}
                          disabled={isLoading(driver.id, 'resume')}
                          className="flex-1 py-1.5 px-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs rounded transition-colors flex items-center justify-center gap-1 font-montserrat"
                        >
                          {isLoading(driver.id, 'resume') ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Play className="w-3 h-3" />
                          )}
                          {isLoading(driver.id, 'resume') ? 'Reanudando...' : 'Reanudar'}
                        </button>
                      </div>

                      {/* Items List */}
                      <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                        {showingDeliveries ? (
                          driverDeliveries.length > 0 ? (
                            driverDeliveries.map((dp) => (
                              <div
                                key={dp.id}
                                className="bg-slate-50 rounded p-2 text-xs"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-bold text-[#B1336E] font-montserrat">
                                    #{dp.id}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 rounded font-montserrat ${getStatusColor(
                                      dp.status
                                    )}`}
                                  >
                                    {dp.status}
                                  </span>
                                </div>
                                <p className="text-slate-700 font-medium font-montserrat">
                                  {truncateName(dp.recipient_name)}
                                </p>
                                {dp.sequence && (
                                  <p className="text-slate-500 text-[10px] font-montserrat">
                                    Secuencia: {dp.sequence}
                                  </p>
                                )}
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-500 text-center py-4 font-montserrat">
                              Sin entregas asignadas
                            </p>
                          )
                        ) : driverOrders.length > 0 ? (
                          driverOrders.map((order) => (
                            <div
                              key={order.id}
                              className="bg-slate-50 rounded p-2 text-xs"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-bold text-[#B1336E] font-montserrat">
                                  #{order.id}
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded font-montserrat ${getStatusColor(
                                    order.status
                                  )}`}
                                >
                                  {order.status}
                                </span>
                              </div>
                              <p className="text-slate-700 font-medium font-montserrat">
                                {truncateName(order.customer_name)}
                              </p>
                              {order.sequence && (
                                <p className="text-slate-500 text-[10px] font-montserrat">
                                  Secuencia: {order.sequence}
                                </p>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-500 text-center py-4 font-montserrat">
                            Sin recojos asignados
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DriversList;